import { memo } from 'react';
import AdminNav from '../admin/components/AdminNav';
import type { BulkRetrySummary, PaymentEventItem, PaymentEventsFilters, PaymentEventProvider } from './adminPaymentEvents.api';
import { PROVIDER_DISPLAY_NAMES } from '../admin/admin.api'; // task-36: shared display names — single source of truth
import './AdminPaymentEvents.css';

interface Props {
  loading: boolean;
  error: string | null;
  events: PaymentEventItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  filters: PaymentEventsFilters;
  onFilterChange: (next: PaymentEventsFilters) => void;
  onApply: () => void;
  onReset: () => void;
  onPageChange: (page: number) => void;
  // task-16: row click opens the detail modal. Optional so older callers/tests
  // that don't wire it stay compatible — rows simply become non-interactive.
  onRowSelect?: (id: string) => void;
  apiKeyMissing?: boolean;
  // task-15: per-row retry wiring. `onRetry` invokes the admin replay endpoint
  // for a single event; `retryingId` is the row id currently in-flight (used
  // to disable the button); `retryFeedback` shows a transient banner with the
  // outcome so staff can confirm success/failure without inspecting the row.
  onRetry?: (event: PaymentEventItem) => void;
  retryingId?: string | null;
  retryFeedback?: { level: 'success' | 'error'; message: string } | null;

  // task-18: bulk-selection props. All optional so existing tests that mount
  // the view without bulk wiring keep rendering — the selection column just
  // becomes inert. The page wires them all up via state setters in the
  // controller.
  selectedIds?: Set<string>;
  onToggleSelected?: (id: string) => void;
  onSelectAllOnPage?: () => void;
  onClearPageSelection?: () => void;
  onClearAllSelection?: () => void;
  onSelectAllMatching?: () => void;
  onRetrySelected?: () => void;
  bulkRunning?: boolean;
  bulkProgress?: { current: number; total: number } | null;
  bulkSummary?: BulkRetrySummary | null;
  bulkError?: string | null;
  selectAllInFlight?: boolean;
  visibleUnprocessedCount?: number;
  supportedProviders?: string[]; // task-36: backend-sourced list; undefined while loading → falls back to empty
}

function fmt(date: string | null | undefined): string {
  if (!date) return '-';
  const d = new Date(date);
  return isNaN(d.getTime()) ? String(date) : d.toLocaleString();
}

function summaryLine(summary: BulkRetrySummary): string {
  // task-18: keep this concise — staff scan the post-batch banner for a quick
  // overview, then dive into per-row error chips for failures.
  const parts = [`Retried ${summary.succeeded} of ${summary.total} successfully`];
  if (summary.failed > 0) parts.push(`${summary.failed} failed`);
  if (summary.skipped > 0) parts.push(`${summary.skipped} skipped (already processed)`);
  if (summary.notFound > 0) parts.push(`${summary.notFound} not found`);
  return parts.join(' · ');
}

const AdminPaymentEventsPageView = memo(function AdminPaymentEventsPageView({
  loading, error, events, pagination, filters, onFilterChange, onApply, onReset, onPageChange,
  onRowSelect, apiKeyMissing, onRetry, retryingId, retryFeedback,
  selectedIds, onToggleSelected, onSelectAllOnPage, onClearPageSelection, onClearAllSelection,
  onSelectAllMatching, onRetrySelected, bulkRunning, bulkProgress, bulkSummary, bulkError,
  selectAllInFlight, visibleUnprocessedCount, supportedProviders = [] // task-36: default empty so .map() is safe while loading
}: Props) {
  // task-18: feature-flag the selection column based on whether the parent
  // wired up the bulk handlers. Older tests or embeddings without bulk
  // support omit these props and get the original column layout.
  const bulkEnabled = !!(onToggleSelected && onRetrySelected);
  const selectedCount = selectedIds ? selectedIds.size : 0;
  const visibleUnprocessed = visibleUnprocessedCount ?? events.filter(ev => !ev.processed).length;
  const allVisibleSelected = bulkEnabled
    && visibleUnprocessed > 0
    && events.filter(ev => !ev.processed).every(ev => selectedIds?.has(ev.id || ev.eventId || ''));
  const someVisibleSelected = bulkEnabled
    && events.filter(ev => !ev.processed).some(ev => selectedIds?.has(ev.id || ev.eventId || ''));

  return (
    <main className="admin-payment-events-container" aria-live="polite">
      <AdminNav />
      <h1 className="admin-payment-events-title">Payment Webhook Events</h1>

      {apiKeyMissing && (
        <div className="admin-payment-events-key-warning">
          Admin routes require an API key. Set VITE_QreditCharge_API_KEY or localStorage.QreditCharge_API_KEY and refresh.
        </div>
      )}

      {error && <div className="admin-payment-events-error" role="alert">Error: {error}</div>}

      {retryFeedback && (
        <div
          className={`admin-payment-events-retry-feedback ${retryFeedback.level}`}
          role={retryFeedback.level === 'error' ? 'alert' : 'status'}
        >
          {retryFeedback.message}
        </div>
      )}

      {bulkError && (
        <div className="admin-payment-events-retry-feedback error" role="alert">
          {bulkError}
        </div>
      )}

      {bulkSummary && !bulkRunning && (
        <div
          className={`admin-payment-events-retry-feedback ${bulkSummary.failed === 0 && bulkSummary.notFound === 0 ? 'success' : 'error'}`}
          role="status"
        >
          {summaryLine(bulkSummary)}
        </div>
      )}

      <form
        className="admin-payment-events-filters"
        onSubmit={(e) => { e.preventDefault(); onApply(); }}
      >
        <label className="admin-payment-events-field">
          Provider
          <select
            className="admin-payment-events-select"
            aria-label="Filter by provider"
            value={filters.provider ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              // task-36: guard uses the dynamic list — no hard-coded names; unknown values collapse to '' (All)
              const provider: PaymentEventProvider | '' = supportedProviders.includes(v) ? v as PaymentEventProvider : '';
              onFilterChange({ ...filters, provider });
            }}
          >
            <option value="">All</option>
            {/* task-36: .map() over the backend-sourced list — adding a provider only updates localVars.ts */}
            {supportedProviders.map((p) => (
              <option key={p} value={p}>{PROVIDER_DISPLAY_NAMES[p] ?? p}</option>
            ))}
          </select>
        </label>

        <label className="admin-payment-events-field">
          Event kind
          <input
            className="admin-payment-events-input"
            type="text"
            placeholder="e.g. subscription_charged"
            value={filters.kind ?? ''}
            onChange={(e) => onFilterChange({ ...filters, kind: e.target.value })}
          />
        </label>

        <label className="admin-payment-events-field">
          Processed
          <select
            className="admin-payment-events-select"
            aria-label="Filter by processed status"
            value={filters.processed ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              const processed: '' | 'true' | 'false' = (v === 'true' || v === 'false') ? v : '';
              onFilterChange({ ...filters, processed });
            }}
          >
            <option value="">All</option>
            <option value="true">Processed</option>
            <option value="false">Unprocessed</option>
          </select>
        </label>

        <label className="admin-payment-events-field">
          Start date
          <input
            className="admin-payment-events-input"
            type="datetime-local"
            value={filters.startDate ?? ''}
            onChange={(e) => onFilterChange({ ...filters, startDate: e.target.value })}
          />
        </label>

        <label className="admin-payment-events-field">
          End date
          <input
            className="admin-payment-events-input"
            type="datetime-local"
            value={filters.endDate ?? ''}
            onChange={(e) => onFilterChange({ ...filters, endDate: e.target.value })}
          />
        </label>

        <button type="submit" className="admin-payment-events-button">Apply</button>
        <button type="button" className="admin-payment-events-button secondary" onClick={onReset}>Reset</button>
      </form>

      {bulkEnabled && (
        <div className="admin-payment-events-bulk-toolbar" role="group" aria-label="Bulk retry actions">
          <span className="admin-payment-events-bulk-count">
            {selectedCount === 0 ? 'No events selected' : `${selectedCount} selected`}
          </span>
          <button
            type="button"
            className="admin-payment-events-button"
            disabled={selectedCount === 0 || bulkRunning}
            onClick={onRetrySelected}
            aria-label={`Retry ${selectedCount} selected events`}
          >
            {bulkRunning && bulkProgress
              ? `Retrying ${bulkProgress.current} of ${bulkProgress.total}…`
              : `Retry selected${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
          </button>
          <button
            type="button"
            className="admin-payment-events-button secondary"
            disabled={bulkRunning || selectAllInFlight}
            onClick={onSelectAllMatching}
            aria-label="Select all unprocessed events matching the current filter"
          >
            {selectAllInFlight ? 'Finding all matching…' : 'Select all matching filter'}
          </button>
          <button
            type="button"
            className="admin-payment-events-button secondary"
            disabled={selectedCount === 0 || bulkRunning}
            onClick={onClearAllSelection}
          >
            Clear selection
          </button>
        </div>
      )}

      {loading ? (
        <div className="admin-payment-events-empty" role="status">Loading payment events…</div>
      ) : events.length === 0 ? (
        <div className="admin-payment-events-empty">No payment events match the current filters.</div>
      ) : (
        <table className="admin-payment-events-table">
          <thead>
            <tr>
              {bulkEnabled && (
                <th scope="col" className="admin-payment-events-th admin-payment-events-th-select">
                  <input
                    type="checkbox"
                    aria-label={allVisibleSelected ? 'Deselect all unprocessed on this page' : 'Select all unprocessed on this page'}
                    disabled={visibleUnprocessed === 0 || bulkRunning}
                    checked={allVisibleSelected}
                    // task-18: indeterminate state communicates "some, but not
                    // all, unprocessed rows on this page are selected" — common
                    // when staff manually toggle individual checkboxes.
                    ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                    onChange={() => {
                      if (allVisibleSelected) onClearPageSelection?.();
                      else onSelectAllOnPage?.();
                    }}
                  />
                </th>
              )}
              <th scope="col" className="admin-payment-events-th">Provider</th>
              <th scope="col" className="admin-payment-events-th">Timestamp</th>
              <th scope="col" className="admin-payment-events-th">Kind</th>
              <th scope="col" className="admin-payment-events-th">Status</th>
              <th scope="col" className="admin-payment-events-th">Payload preview</th>
              <th scope="col" className="admin-payment-events-th">Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => {
              // task-15 + task-16: prefer the listing-emitted `id` (Mongo `_id`
              // online, or `provider:eventId` composite offline) over the bare
              // `eventId`, since `eventId` is intentionally NOT unique across
              // providers. Both the row click (detail modal) and the retry
              // button (in-flight guard) must address rows by this stable
              // identifier.
              const rowId = ev.id || ev.eventId;
              const interactive = !!onRowSelect && !!rowId;
              const isRetrying = retryingId !== undefined && retryingId !== null && retryingId === rowId;
              // task-15: retry only makes sense for events that have NOT
              // already succeeded — replaying a successful billing webhook
              // could double-credit a user. Mirror the server-side guard.
              const canRetry = !ev.processed && Boolean(onRetry) && !!rowId;
              const isSelected = bulkEnabled && !!rowId && selectedIds?.has(rowId) === true;
              return (
                <tr
                  key={rowId}
                  className={interactive ? 'admin-payment-events-row-clickable' : undefined}
                  onClick={interactive ? () => onRowSelect!(rowId) : undefined}
                  // Keyboard parity with the click handler so the row is reachable for screen-reader / keyboard users
                  tabIndex={interactive ? 0 : undefined}
                  role={interactive ? 'button' : undefined}
                  aria-label={interactive ? `Open detail for ${ev.provider} event ${ev.kind || rowId}` : undefined}
                  onKeyDown={interactive ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowSelect!(rowId); }
                  } : undefined}
                >
                  {bulkEnabled && (
                    <td
                      className="admin-payment-events-td admin-payment-events-td-select"
                      // task-18: don't open the detail modal when staff click
                      // the checkbox or hit space/enter on it.
                      onClick={interactive ? (e) => e.stopPropagation() : undefined}
                      onKeyDown={interactive ? (e) => e.stopPropagation() : undefined}
                    >
                      {ev.processed ? (
                        <span className="admin-payment-events-empty-action" aria-hidden="true">—</span>
                      ) : (
                        <input
                          type="checkbox"
                          aria-label={`Select event ${ev.kind || rowId} for bulk retry`}
                          checked={isSelected}
                          disabled={!rowId || bulkRunning}
                          onChange={() => rowId && onToggleSelected?.(rowId)}
                        />
                      )}
                    </td>
                  )}
                  <td className="admin-payment-events-td">
                    <span className={`admin-payment-events-badge provider-${ev.provider}`}>{ev.provider}</span>
                  </td>
                  <td className="admin-payment-events-td">{fmt(ev.timestamp)}</td>
                  <td className="admin-payment-events-td">{ev.kind || '-'}</td>
                  <td className="admin-payment-events-td">
                    <span className={`admin-payment-events-badge ${ev.processed ? 'processed' : 'unprocessed'}`}>
                      {ev.processed ? 'Processed' : 'Unprocessed'}
                    </span>
                    {ev.processingAttempts !== undefined && ev.processingAttempts > 0 && (
                      <div className="admin-payment-events-attempts" title="Number of processing attempts so far">
                        Attempts: {ev.processingAttempts}
                      </div>
                    )}
                    {ev.lastProcessingError && (
                      <div className="admin-payment-events-payload" title={ev.lastProcessingError}>
                        {ev.lastProcessingError}
                      </div>
                    )}
                  </td>
                  <td className="admin-payment-events-td">
                    <pre className="admin-payment-events-payload">{ev.payloadPreview || '(empty)'}</pre>
                  </td>
                  <td
                    className="admin-payment-events-td"
                    // task-15 + task-16: stop the row's click/keydown from
                    // bubbling out of the action cell so clicking Retry does
                    // NOT also open the detail modal.
                    onClick={interactive ? (e) => e.stopPropagation() : undefined}
                    onKeyDown={interactive ? (e) => e.stopPropagation() : undefined}
                  >
                    {canRetry ? (
                      <button
                        type="button"
                        className="admin-payment-events-button"
                        aria-label={`Retry payment event ${ev.kind || ev.eventId}`}
                        disabled={isRetrying || bulkRunning}
                        onClick={() => onRetry && onRetry(ev)}
                      >
                        {isRetrying ? 'Retrying…' : 'Retry'}
                      </button>
                    ) : (
                      <span className="admin-payment-events-empty-action" aria-hidden="true">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="admin-payment-events-pagination">
        <button
          type="button"
          className="admin-payment-events-button secondary"
          disabled={pagination.page <= 1 || loading}
          onClick={() => onPageChange(pagination.page - 1)}
        >Previous</button>
        <span>
          Page {pagination.page}
          {pagination.totalPages > 0 ? ` of ${pagination.totalPages}` : ''} · {pagination.total} total
        </span>
        <button
          type="button"
          className="admin-payment-events-button secondary"
          disabled={loading || pagination.totalPages === 0 || pagination.page >= pagination.totalPages}
          onClick={() => onPageChange(pagination.page + 1)}
        >Next</button>
      </div>
    </main>
  );
});

export default AdminPaymentEventsPageView;
